"use client";
import { NotLoggedIn } from "@components/ui/not-logged-in/NotLoggedIn";
import { MyAccountSkeleton } from "@components/ui/skeletons/my-account-skeleton";
import { ProfileList } from "@components/user/profile/profile-list";
import { ProfileOverview } from "@components/user/profile/profile-overview";
import { getCountry } from "@config/site/site-config";
import { useAuth } from "@lib/auth/useAuth";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { isCountryWithReverseNameOrder } from "@lib/utils/address";
import { dateHandlers } from "@lib/utils/dateutils";
import { useGetMyProfile } from "@store/hooks";
import { Spinner } from "@ecco/ui/spinner";
import { type AccountPageProps } from "@templates/AccountPage";
import { TwoColumnsPage } from "@templates/TwoColumnPage";

type ProfileClientProps = Pick<AccountPageProps, "page">;

export default function ProfileClient({ page }: ProfileClientProps) {
    const { data, isLoading: isLoadingProfile } = useGetMyProfile();
    const { isLoggedIn, isLoading } = useAuth();
    const { getMultiple } = useMicrocopy();

    const microcopies = getMultiple(CommonCMS.page, [
        CommonCMS.profile.birthDateLabel,
        CommonCMS.profile.fullNameLabel,
        CommonCMS.profile.emailLabel,
    ]);
    const birthDateLabel = microcopies[CommonCMS.profile.birthDateLabel];
    const fullNameLabel = microcopies[CommonCMS.profile.fullNameLabel];
    const emailLabel = microcopies[CommonCMS.profile.emailLabel];

    const dateformat = "DD/MM/YYYY";
    const firstName = data?.firstName || "";
    const lastName = data?.lastName || "";
    const email = data?.email || "";
    const date = data?.dateOfBirth ? new Date(data?.dateOfBirth) : null;
    const convertedDateOfBirth = date
        ? dateHandlers.defaultConvertFromCTFormat(dateformat, data.dateOfBirth)
        : "";
    const fullName = isCountryWithReverseNameOrder(getCountry)
        ? `${lastName} ${firstName}`.trim()
        : `${firstName} ${lastName}`.trim();

    const overviewData: Record<string, string> = {};

    overviewData[birthDateLabel] = convertedDateOfBirth || "";
    overviewData[fullNameLabel] = fullName;
    overviewData[emailLabel] = email;
    const navigationTitle = fullName;

    if (isLoading || isLoadingProfile) {
        return <MyAccountSkeleton />;
    }

    if (!isLoggedIn) {
        return (
            <>
                <div hidden data-testid="myAccountProfile" />
                <NotLoggedIn />
            </>
        );
    }

    return (
        <>
            <div hidden data-testid="myAccountProfile" />
            <TwoColumnsPage
                showTitle={false}
                navigationTitle={navigationTitle}
                page={page}
                showNavigation={false}
                leftColumn={{
                    prefix: data ? (
                        <ProfileList data={{ ...data, convertedDateOfBirth, emailLabel }} />
                    ) : (
                        <Spinner my="25%" mx="50%" />
                    ),
                }}
                rightColumn={{
                    prefix: <ProfileOverview fields={overviewData} />,
                }}
            />
        </>
    );
}
